import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import AlgemeenTab from './LocatieInfo/AlgemeenTab';
import NutsvoorzieningenTab from './LocatieInfo/NutsvoorzieningenTab';
import VoorraadTab from './LocatieInfo/VoorraadTab';
import LeveranciersTab from './LocatieInfo/LeveranciersTab';
import ApparatuurTab from './LocatieInfo/ApparatuurTab';
import ContactpersonenTab from './LocatieInfo/ContactpersonenTab';
import NoodproceduresTab from './LocatieInfo/NoodproceduresTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocatieInfo = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const { canManage, isAdmin: isAdminFn } = useAuth();
  // 4.8a/b: manager+ mag bedrijfsgegevens, alleen admin mag het Bovemij-nummer
  const magBeheren = canManage();
  const isAdmin = isAdminFn();
  const [searchParams, setSearchParams] = useSearchParams();
  const [locatieData, setLocatieData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Controlled tab state - get from URL or default to "algemeen"
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'algemeen');

  // Handle tab change - update URL without full page reload
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab }, { replace: true });
  };

  useEffect(() => {
    if (selectedLocatie) {
      fetchLocatieData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocatie]);

  const fetchLocatieData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/locaties/${selectedLocatie}`);
      setLocatieData(response.data);
    } catch (error) {
      console.error('Error fetching locatie data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!locatieData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Geen locatie geselecteerd</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Locatie Info - {selectedLocatieData?.naam}</h1>
        <p className="text-slate-500">Alle gedetailleerde informatie over deze locatie</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        {/* Scrollable tabs container for mobile */}
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          <TabsList className="bg-white border border-slate-200 h-auto p-1 inline-flex min-w-max md:flex md:flex-wrap md:min-w-0">
            <TabsTrigger value="algemeen" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">Algemeen</TabsTrigger>
            <TabsTrigger value="nuts" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
              <span className="hidden sm:inline">Nutsvoorzieningen</span>
              <span className="sm:hidden">Nuts</span>
            </TabsTrigger>
            <TabsTrigger value="voorraad" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
              Voorraad
              {locatieData.voorraad?.length > 0 && (
                <span className="ml-1 sm:ml-2 bg-blue-100 text-blue-700 px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                  {locatieData.voorraad.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="leveranciers" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
              <span className="hidden sm:inline">Leveranciers</span>
              <span className="sm:hidden">Lev.</span>
              {locatieData.leveranciers?.length > 0 && (
                <span className="ml-1 sm:ml-2 bg-cyan-100 text-cyan-700 px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                  {locatieData.leveranciers.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="apparatuur" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
              <span className="hidden sm:inline">Apparatuur</span>
              <span className="sm:hidden">App.</span>
              {locatieData.apparatuur?.length > 0 && (
                <span className="ml-1 sm:ml-2 bg-orange-100 text-orange-700 px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                  {locatieData.apparatuur.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacten" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
              <span className="hidden sm:inline">Contactpersonen</span>
              <span className="sm:hidden">Contact</span>
              {locatieData.contactpersonen?.length > 0 && (
                <span className="ml-1 sm:ml-2 bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                  {locatieData.contactpersonen.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="nood" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
              <span className="hidden sm:inline">Noodprocedures</span>
              <span className="sm:hidden">Nood</span>
              {locatieData.noodprocedures?.length > 0 && (
                <span className="ml-1 sm:ml-2 bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                  {locatieData.noodprocedures.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="algemeen">
          <AlgemeenTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
            isAdmin={isAdmin}
            magBeheren={magBeheren}
          />
        </TabsContent>

        <TabsContent value="nuts">
          <NutsvoorzieningenTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
          />
        </TabsContent>

        <TabsContent value="voorraad">
          <VoorraadTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
            onVoorraadItemGewijzigd={(voorraadId, veld, waarde) => setLocatieData(prev => (
              // 6.6: direct in de ouderstate, dus een tabwissel (Radix unmount)
              // of de telling-modal ziet nooit meer de oude waarde
              prev ? {
                ...prev,
                voorraad: (prev.voorraad || []).map(it => (
                  it._id === voorraadId ? { ...it, [veld]: waarde } : it
                )),
              } : prev
            ))}
          />
        </TabsContent>

        <TabsContent value="leveranciers">
          <LeveranciersTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
          />
        </TabsContent>

        <TabsContent value="apparatuur">
          <ApparatuurTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
          />
        </TabsContent>

        <TabsContent value="contacten">
          <ContactpersonenTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
          />
        </TabsContent>

        <TabsContent value="nood">
          <NoodproceduresTab
            locatieId={selectedLocatie}
            locatieData={locatieData}
            onUpdate={fetchLocatieData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LocatieInfo;
